import React, { useState, useEffect, useMemo } from 'react';
import {
  Package,
  Truck,
  Clock,
  BarChart3,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Info,
  List,
  Hash,
  Loader2
} from 'lucide-react';
import {
  OptimizationResults as OptimizationResultsType,
  OptimizedContainer,
  OptimizedOrder,
  ArticleAggregatedOrder
} from '../types/optimization';
import OptimizationService from '../services/optimizationService';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface OptimizationResultsProps {
  results: OptimizationResultsType;
  onContainerEdit: (container: OptimizedContainer) => void;
  onContainerDelete: (containerId: string) => void;
  onOrderEdit: (order: OptimizedOrder, containerId: string) => void;
}

type ArticleSortKey = 'article_ref' | 'supplier_name' | 'total_quantity' | 'total_volume_m3' | 'projected_coverage' | 'current_coverage' | 'moq';

const OptimizationResults: React.FC<OptimizationResultsProps> = ({
  results,
  onContainerEdit,
  onContainerDelete,
  onOrderEdit
}) => {
  const { activeDatabase } = useDatabaseContext();
  const [data, setData] = useState<OptimizationResultsType>(results);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [toasts, setToasts] = useState<{ id: number; type: 'success' | 'error' | 'info'; message: string }[]>([]);

  useEffect(() => {
    setData(results);
  }, [results]);
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'containers' | 'articles'>('containers');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [sort, setSort] = useState<{ key: ArticleSortKey; dir: 'asc' | 'desc' }>({ key: 'article_ref', dir: 'asc' });
  
  const summary = OptimizationService.calculateSummary(data);
  const aggregatedRaw: ArticleAggregatedOrder[] = useMemo(() => OptimizationService.aggregateByArticle(data), [data]);
  const aggregated: ArticleAggregatedOrder[] = useMemo(() => {
    const data = [...aggregatedRaw];
    data.sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'article_ref':
          return a.article_ref.localeCompare(b.article_ref) * dir;
        case 'supplier_name':
          return a.supplier_name.localeCompare(b.supplier_name) * dir;
        case 'total_quantity':
          return (a.total_quantity - b.total_quantity) * dir;
        case 'total_volume_m3':
          return (a.total_volume_m3 - b.total_volume_m3) * dir;
        case 'projected_coverage':
          return ((a.projected_coverage || 0) - (b.projected_coverage || 0)) * dir;
        case 'current_coverage':
          return ((a.current_coverage || 0) - (b.current_coverage || 0)) * dir;
        case 'moq':
          return (((a.moq ?? 0) - (b.moq ?? 0))) * dir;
        default:
          return 0;
      }
    });
    return data;
  }, [aggregatedRaw, sort]);
  const displayIndex = useMemo(() => OptimizationService.buildContainerDisplayIndex(data.containers || []), [data]);

  const computeContainerStatus = (orders: OptimizedOrder[]): 'pending' | 'ordered' | 'delivered' | 'cancelled' => {
    const statuses = orders.map(o => o.delivery_status || 'pending');
    if (statuses.length === 0) return 'pending';
    if (statuses.every(s => s === 'delivered')) return 'delivered';
    if (statuses.every(s => s === 'cancelled')) return 'cancelled';
    if (statuses.some(s => s === 'ordered')) return 'ordered';
    return 'pending';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'delivered':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'ordered':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-4 h-4" />;
      case 'ordered':
        return <Truck className="w-4 h-4" />;
      case 'cancelled':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'delivered':
        return 'Validé';
      case 'ordered':
        return 'Commandé';
      case 'cancelled':
        return 'Annulé';
      default:
        return 'En attente';
    }
  };

  const refreshResults = async () => {
    if (!activeDatabase?.id) return;
    const updated = await OptimizationService.getOptimizationResults(
      String(activeDatabase.id),
      String(data.optimization_run.id)
    );
    setData(updated);
  };

  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  };

  const runWithRefresh = async (fn: () => Promise<any>) => {
    try {
      setIsUpdating(true);
      await fn();
      await refreshResults();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRunStatus = async (status: 'pending' | 'ordered' | 'delivered' | 'cancelled') => {
    if (!activeDatabase?.id) return;
    try {
      await runWithRefresh(() => OptimizationService.setRunStatus(String(activeDatabase.id), String(data.optimization_run.id), { status, force: true }));
      const label = status === 'ordered' ? 'Commandé' : status === 'delivered' ? 'Validé' : status === 'cancelled' ? 'Annulé' : 'En attente';
      showToast('success', `Run: ${label}`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Erreur lors de la mise à jour du run');
    }
  };

  const handleContainerStatus = async (containerId: string, status: 'pending' | 'ordered' | 'delivered' | 'cancelled') => {
    if (!activeDatabase?.id) return;
    try {
      await runWithRefresh(() => OptimizationService.setContainerStatus(String(activeDatabase.id), String(containerId), { status, force: true }));
      const label = status === 'ordered' ? 'Commandé' : status === 'delivered' ? 'Validé' : status === 'cancelled' ? 'Annulé' : 'En attente';
      showToast('success', `Container #${displayIndex[containerId]}: ${label}`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Erreur lors de la mise à jour du container');
    }
  };

  const handleOrderStatus = async (orderId: string, status: 'pending' | 'ordered' | 'delivered' | 'cancelled') => {
    if (!activeDatabase?.id) return;
    try {
      await runWithRefresh(() => OptimizationService.setOrderStatus(String(activeDatabase.id), String(orderId), { status, force: true }));
      const label = status === 'ordered' ? 'Commandé' : status === 'delivered' ? 'Validé' : status === 'cancelled' ? 'Annulé' : 'En attente';
      showToast('success', `Ligne ${orderId}: ${label}`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Erreur lors de la mise à jour de la ligne');
    }
  };

  const SortableHeader: React.FC<{ label: string; sortKey: ArticleSortKey; align?: 'left' | 'right' }> = ({ label, sortKey, align = 'left' }) => (
    <th className={`px-4 py-2 text-${align} cursor-pointer select-none`} onClick={() => setSort(prev => ({ key: sortKey, dir: prev.key === sortKey && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === sortKey && (
          <span className="text-gray-500 text-xs">{sort.dir === 'asc' ? '▲' : '▼'}</span>
        )}
      </span>
    </th>
  );

  // Simple modal confirm dialog
  const ConfirmDialog: React.FC<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onClose: () => void;
  }> = ({ open, title, message, confirmLabel = 'Confirmer', cancelLabel = 'Annuler', onConfirm, onClose }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/30" onClick={onClose} />
        <div className="relative bg-white rounded-md shadow-lg border w-full max-w-sm mx-4 p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-700 mb-4">{message}</p>
          <div className="flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border text-gray-700 bg-gray-50 hover:bg-gray-100">{cancelLabel}</button>
            <button onClick={() => { onConfirm(); onClose(); }} className="px-3 py-1.5 text-sm rounded border text-white bg-blue-600 hover:bg-blue-700">{confirmLabel}</button>
          </div>
        </div>
      </div>
    );
  };

  const ConfirmActionButton: React.FC<{
    title: string;
    message: string;
    onConfirm: () => Promise<any> | void;
    className: string;
    disabled?: boolean;
    children: React.ReactNode;
  }> = ({ title, message, onConfirm, className, disabled, children }) => {
    const [open, setOpen] = useState(false);
    return (
      <>
        <button
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={className}
          title={title}
        >
          {children}
        </button>
        <ConfirmDialog
          open={open}
          title={title}
          message={message}
          onClose={() => setOpen(false)}
          onConfirm={async () => {
            await onConfirm();
          }}
        />
      </>
    );
  };

  return (
    <div className="space-y-4">{/* compact spacing */}
      {isUpdating && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 bg-white rounded-md border shadow">
          <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
          <span className="text-sm text-gray-700">Mise à jour...</span>
        </div>
      )}
      {/* Toasts */}
      <div className="fixed top-16 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border shadow text-sm ${
            t.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' :
            t.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' :
            'bg-blue-50 text-blue-800 border-blue-200'
          }`}>
            {t.type === 'success' ? <CheckCircle className="w-4 h-4" /> : t.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <Info className="w-4 h-4" />}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
      {/* Run-level actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Run #{data.optimization_run.id}
        </div>
        <div className="flex items-center gap-2">
          <ConfirmActionButton
            disabled={isUpdating}
            title="Marquer tout le run comme commandé"
            message="Confirmez-vous le passage à l'état 'Commandé' pour toutes les lignes du run ?"
            className="px-2 py-1 text-xs rounded border bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1"
            onConfirm={() => handleRunStatus('ordered')}
          >
            <><Truck className="w-4 h-4" /> Commandé</>
          </ConfirmActionButton>
          <ConfirmActionButton
            disabled={isUpdating}
            title="Valider toutes les commandes du run"
            message="Confirmez-vous la validation de toutes les lignes du run ?"
            className="px-2 py-1 text-xs rounded border bg-green-50 text-green-700 hover:bg-green-100 flex items-center gap-1"
            onConfirm={() => handleRunStatus('delivered')}
          >
            <><CheckCircle className="w-4 h-4" /> Valider</>
          </ConfirmActionButton>
          <ConfirmActionButton
            disabled={isUpdating}
            title="Remettre en attente"
            message="Confirmez-vous le passage à l'état 'En attente' pour toutes les lignes du run ?"
            className="px-2 py-1 text-xs rounded border bg-yellow-50 text-yellow-700 hover:bg-yellow-100 flex items-center gap-1"
            onConfirm={() => handleRunStatus('pending')}
          >
            <><Clock className="w-4 h-4" /> En attente</>
          </ConfirmActionButton>
          <ConfirmActionButton
            disabled={isUpdating}
            title="Annuler toutes les commandes du run"
            message="Confirmez-vous l'annulation de toutes les lignes du run ?"
            className="px-2 py-1 text-xs rounded border bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1"
            onConfirm={() => handleRunStatus('cancelled')}
          >
            <><AlertCircle className="w-4 h-4" /> Annuler</>
          </ConfirmActionButton>
        </div>
      </div>
      {/* Summary Cards (compact) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{/* fewer gaps */}
        <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
          <div className="flex items-center gap-2">
            <Package className="text-blue-600" size={18} />
            <div>
              <p className="text-xs text-blue-700">Containers totaux</p>
              <p className="text-xl font-semibold text-blue-900">
                {summary.total_containers}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-3 rounded-md border border-green-200">
          <div className="flex items-center gap-2">
            <BarChart3 className="text-green-600" size={18} />
            <div>
              <p className="text-xs text-green-700">Volume total</p>
              <p className="text-xl font-semibold text-green-900">
                {OptimizationService.formatVolume(summary.total_volume_m3)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
          <div className="flex items-center gap-2">
            <Truck className="text-purple-600" size={18} />
            <div>
              <p className="text-xs text-purple-700">Taux remplissage moy.</p>
              <p className="text-xl font-semibold text-purple-900">
                {OptimizationService.formatFillRate(summary.average_fill_rate)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
          <div className="flex items-center gap-2">
            <Clock className="text-orange-600" size={18} />
            <div>
              <p className="text-xs text-orange-700">Délai max livraison</p>
              <p className="text-xl font-semibold text-orange-900">
                {summary.estimated_delivery_range.max_months} mois
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          className={`px-3 py-2 text-sm ${activeTab === 'containers' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-600'}`}
          onClick={() => setActiveTab('containers')}
        >
          <Package className="inline mr-1" size={14} /> Containers
        </button>
        <button
          className={`px-3 py-2 text-sm ${activeTab === 'articles' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-600'}`}
          onClick={() => setActiveTab('articles')}
        >
          <List className="inline mr-1" size={14} /> Articles
        </button>
      </div>

      {activeTab === 'containers' ? (
        <div className="bg-white rounded-md shadow">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">Containers optimisés</h3>
          </div>

          <div className="divide-y divide-gray-200">
            {data.containers.map((container) => (
              <div key={container.id} className="p-4">{/* compact paddings */}
                {/* Container Header */}
                <div className="flex items-center justify-between mb-3">{/* compact margin */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Package className="text-blue-600" size={18} />
                      <h4 className="text-base font-medium text-gray-900">
                        {OptimizationService.formatContainerType(container.container_type)}
                      </h4>
                      <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700">
                        {container.location_group}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                        <Hash size={12} /> {displayIndex[container.id]}
                      </span>
                    </div>

                    {(() => {
                      const contStatus = computeContainerStatus(container.orders || []);
                      return (
                        <div className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${getStatusColor(contStatus)}`}>
                          {getStatusIcon(contStatus)}
                          {getStatusLabel(contStatus)}
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onContainerEdit(container)}
                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Modifier le container"
                    >
                      <Edit size={14} />
                    </button>
                    <ConfirmActionButton
                      disabled={isUpdating}
                      title="Marquer le container comme commandé"
                      message={`Confirmer le passage à 'Commandé' du container #${displayIndex[container.id]} ?`}
                      className="p-1.5 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded"
                      onConfirm={() => handleContainerStatus(String(container.id), 'ordered')}
                    >
                      <Truck size={14} />
                    </ConfirmActionButton>
                    <ConfirmActionButton
                      disabled={isUpdating}
                      title="Valider le container"
                      message={`Confirmer la validation du container #${displayIndex[container.id]} ?`}
                      className="p-1.5 text-green-700 hover:text-green-800 hover:bg-green-50 rounded"
                      onConfirm={() => handleContainerStatus(String(container.id), 'delivered')}
                    >
                      <CheckCircle size={14} />
                    </ConfirmActionButton>
                    <ConfirmActionButton
                      disabled={isUpdating}
                      title="Remettre le container en attente"
                      message={`Confirmer le passage à 'En attente' du container #${displayIndex[container.id]} ?`}
                      className="p-1.5 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50 rounded"
                      onConfirm={() => handleContainerStatus(String(container.id), 'pending')}
                    >
                      <Clock size={14} />
                    </ConfirmActionButton>
                    <ConfirmActionButton
                      disabled={isUpdating}
                      title="Annuler le container"
                      message={`Confirmer l'annulation du container #${displayIndex[container.id]} ?`}
                      className="p-1.5 text-red-700 hover:text-red-800 hover:bg-red-50 rounded"
                      onConfirm={() => handleContainerStatus(String(container.id), 'cancelled')}
                    >
                      <AlertCircle size={14} />
                    </ConfirmActionButton>
                    <button
                      onClick={() => setSelectedContainer(
                        selectedContainer === container.id ? null : container.id
                      )}
                      className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                      title="Voir les détails"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => onContainerDelete(container.id)}
                      className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Supprimer le container"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Container Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">{/* add id/type/location line */}
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-xs text-gray-600">Identifiant</p>
                    <p className="text-sm font-medium">#{displayIndex[container.id]}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Type</p>
                    <p className="text-sm font-medium">{container.container_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Lieu</p>
                    <p className="text-sm font-medium">{container.location_group}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Volume utilisé</p>
                    <p className="text-sm font-medium">
                      {OptimizationService.formatVolume(container.total_volume_m3)} / {' '}
                      {OptimizationService.formatVolume(container.capacity_m3)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Taux de remplissage</p>
                    <p className="text-sm font-medium">
                      {OptimizationService.formatFillRate(container.fill_rate)}
                    </p>
                  </div>
                </div>

                {/* Container Details (Expandable) */}
                {selectedContainer === container.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">
                      Commandes détaillées
                    </h5>
                    <div className="space-y-2">
                      {container.orders.map((order) => (
                        <div
                          key={order.id}
                          className="flex items-center justify-between p-2 bg-white rounded border"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <p className="font-medium text-gray-900 text-sm">
                                {order.article_ref}
                              </p>
                              <span className="text-xs text-gray-600">
                                de {order.supplier_name}
                              </span>
                              <div className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded border ${getStatusColor(order.delivery_status)}`}>
                                {getStatusIcon(order.delivery_status)}
                                {getStatusLabel(order.delivery_status)}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">{/* include container id */}
                              <div>
                                <p className="text-gray-600">Container</p>
                                <p className="font-medium">#{displayIndex[container.id]} · {container.container_type} · {container.location_group}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Quantité</p>
                                <p className="font-medium">{order.quantity_to_order.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-600">Volume</p>
                                <p className="font-medium">
                                  {OptimizationService.formatVolume(order.volume_m3)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Couverture actuelle</p>
                                <p className="font-medium">
                                  {OptimizationService.formatCoverage(order.current_coverage)}
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-600">Couverture projetée</p>
                                <p className="font-medium text-green-600">
                                  {OptimizationService.formatCoverage(order.projected_coverage)}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="ml-3 flex items-center gap-1">
                            <button
                              onClick={() => onOrderEdit(order, container.id)}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Modifier la commande"
                            >
                              <Edit size={14} />
                            </button>
                            <ConfirmActionButton
                              disabled={isUpdating}
                              title="Marquer la ligne comme commandée"
                              message={`Confirmer le passage à 'Commandé' pour la ligne ${order.id} ?`}
                              className="p-1.5 text-blue-700 hover:text-blue-800 hover:bg-blue-50 rounded"
                              onConfirm={() => handleOrderStatus(String(order.id), 'ordered')}
                            >
                              <Truck size={14} />
                            </ConfirmActionButton>
                            <ConfirmActionButton
                              disabled={isUpdating}
                              title="Valider la ligne"
                              message={`Confirmer la validation pour la ligne ${order.id} ?`}
                              className="p-1.5 text-green-700 hover:text-green-800 hover:bg-green-50 rounded"
                              onConfirm={() => handleOrderStatus(String(order.id), 'delivered')}
                            >
                              <CheckCircle size={14} />
                            </ConfirmActionButton>
                            <ConfirmActionButton
                              disabled={isUpdating}
                              title="Remettre la ligne en attente"
                              message={`Confirmer le passage à 'En attente' pour la ligne ${order.id} ?`}
                              className="p-1.5 text-yellow-700 hover:text-yellow-800 hover:bg-yellow-50 rounded"
                              onConfirm={() => handleOrderStatus(String(order.id), 'pending')}
                            >
                              <Clock size={14} />
                            </ConfirmActionButton>
                            <ConfirmActionButton
                              disabled={isUpdating}
                              title="Annuler la ligne"
                              message={`Confirmer l'annulation pour la ligne ${order.id} ?`}
                              className="p-1.5 text-red-700 hover:text-red-800 hover:bg-red-50 rounded"
                              onConfirm={() => handleOrderStatus(String(order.id), 'cancelled')}
                            >
                              <AlertCircle size={14} />
                            </ConfirmActionButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-md shadow">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Articles commandés</h3>
            <span className="text-xs text-gray-500">{aggregated.length} articles</span>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <SortableHeader label="Article" sortKey="article_ref" />
                  <SortableHeader label="Fournisseur" sortKey="supplier_name" />
                  <SortableHeader label="Quantité totale" sortKey="total_quantity" align="right" />
                  <SortableHeader label="Volume total" sortKey="total_volume_m3" align="right" />
                  <SortableHeader label="MOQ" sortKey="moq" align="right" />
                  <SortableHeader label="Couv. actuelle" sortKey="current_coverage" />
                  <SortableHeader label="Couv. projetée" sortKey="projected_coverage" />
                  <th className="text-left px-4 py-2">Répartition containers</th>
                </tr>
              </thead>
              <tbody>
                {aggregated.map(row => {
                  const rowKey = `${row.supplier_name}-${row.article_ref}`;
                  const isExpanded = !!expandedRows[rowKey];
                  // Group by location/type for compact view
                  const grouped = row.containers_split.reduce<Record<string, { location: string; type: string; count: number; qty: number }>>((acc, s) => {
                    const key = `${s.location_group}::${s.container_type}`;
                    if (!acc[key]) acc[key] = { location: s.location_group, type: s.container_type, count: 0, qty: 0 };
                    acc[key].count += 1;
                    acc[key].qty += s.quantity;
                    return acc;
                  }, {});
                  return (
                    <tr key={rowKey} className="border-t">
                      <td className="px-4 py-2 font-medium text-gray-900">{row.article_ref}</td>
                      <td className="px-4 py-2 text-gray-700">{row.supplier_name}</td>
                      <td className="px-4 py-2 text-right">{row.total_quantity.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">{OptimizationService.formatVolume(row.total_volume_m3)}</td>
                      <td className="px-4 py-2 text-right">{row.moq !== undefined ? row.moq.toLocaleString() : '-'}</td>
                      <td className="px-4 py-2">{row.current_coverage !== undefined ? OptimizationService.formatCoverage(row.current_coverage) : '-'}</td>
                      <td className="px-4 py-2 text-green-700">{row.projected_coverage !== undefined ? OptimizationService.formatCoverage(row.projected_coverage) : '-'}</td>
                      <td className="px-4 py-2">
                        {!isExpanded ? (
                          <div className="flex flex-wrap gap-1 items-center">
                            {Object.values(grouped).map(g => (
                              <span key={`${g.location}-${g.type}`} className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border">
                                {g.location} · {g.type} × {g.count} ({g.qty.toLocaleString()})
                              </span>
                            ))}
                            {row.containers_split.length > 0 && (
                              <button
                                className="ml-1 text-xs text-blue-600 hover:underline"
                                onClick={() => setExpandedRows(prev => ({ ...prev, [rowKey]: true }))}
                                title="Voir le détail des containers"
                              >
                                Voir détails
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1 items-center">
                            {row.containers_split.map(split => (
                              <span key={`${rowKey}-${split.container_id}`} className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border">
                                #{split.display_index} · {split.container_type} · {split.location_group}: {split.quantity.toLocaleString()}
                              </span>
                            ))}
                            <button
                              className="ml-1 text-xs text-blue-600 hover:underline"
                              onClick={() => setExpandedRows(prev => ({ ...prev, [rowKey]: false }))}
                              title="Masquer les détails"
                            >
                              Masquer
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {data.containers.length === 0 && (
        <div className="text-center py-10">{/* compact */}
          <Info className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Aucun container optimisé
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            L'optimisation n'a généré aucune commande. Vérifiez les paramètres et les données.
          </p>
        </div>
      )}
    </div>
  );
};

export default OptimizationResults;
